import type { ReactNode } from 'react'
import { legalPages } from '@publiqa/shared'
import type { Page, SitemapBlock } from '@publiqa/shared'
import { useFetch } from '../lib/useFetch'
import { Blocks, SectionHead } from './blocks/Blocks'
import { SmartLink, usePublic, usePubliqApi } from './context'
import { useHead } from './Layout'
import { Loading } from './ui'

/**
 * Pages légales et plan du site, liées depuis le pied de page de tous les
 * sites. Ce sont des pages ordinaires (Pages du back-office), créées avec
 * chaque site et éditables par son responsable. Si l'une d'elles manque
 * (supprimée), le modèle neutre s'affiche à sa place.
 */

export type LegalKind = 'mentions' | 'cgu' | 'confidentialite' | 'plan'

export const LEGAL_LINKS: { kind: LegalKind; slug: string; label: string }[] = [
  { kind: 'mentions', slug: 'mentions-legales', label: 'Mentions légales' },
  { kind: 'cgu', slug: 'cgu', label: 'CGU' },
  { kind: 'confidentialite', slug: 'confidentialite', label: 'Politique de confidentialité' },
  { kind: 'plan', slug: 'plan-du-site', label: 'Plan du site' },
]

export function LegalRoute({ kind }: { kind: LegalKind }) {
  const slug = LEGAL_LINKS.find((l) => l.kind === kind)!.slug
  const path = usePubliqApi()
  const custom = useFetch<{ page: Page }>(path(`/pages/${slug}`))
  const template = legalPages().find((p) => p.slug === slug)!
  const page = custom.data?.page
  const title = page?.title ?? template.title
  useHead(title)
  if (custom.loading && !custom.data && !custom.error) return <Loading />
  const blocks = page?.blocks ?? template.blocks
  return (
    <>
      {blocks[0]?.type !== 'hero' && (
        <header className="page-title container">
          <h1>{title}</h1>
        </header>
      )}
      <div className="legal">
        <Blocks blocks={blocks} pageId={page?.id ?? ''} />
      </div>
    </>
  )
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h2>{title}</h2>
      {children}
    </section>
  )
}

/** Bloc « Plan du site » : la liste se construit à partir du site lui-même. */
export function SitemapView({ b }: { b: SitemapBlock }) {
  const { publication, pages, categories } = usePublic()
  const sections = publication.settings.nav.filter((l) => l.href.startsWith('/#'))
  const legal = new Set<string>(LEGAL_LINKS.map((l) => l.slug))
  const others = pages.filter((p) => !p.isHome && !legal.has(p.slug))
  return (
    <section className="b-section b-sitemap">
      <div className="container narrow">
        {(b.title || b.intro) && <SectionHead title={b.title} intro={b.intro} />}
        <div className="legal-map">
          <Group title="Pages">
            <ul>
              <li>
                <SmartLink href="/">Accueil</SmartLink>
              </li>
              {sections.map((l) => (
                <li key={l.href} className="legal-map-sub">
                  <SmartLink href={l.href}>{l.label}</SmartLink>
                </li>
              ))}
              {others.map((p) => (
                <li key={p.id}>
                  <SmartLink href={`/${p.slug}`}>{p.title}</SmartLink>
                </li>
              ))}
              {(publication.kind === 'magazine' || categories.length > 0) && (
                <li>
                  <SmartLink href="/actualites">Toutes les actualités</SmartLink>
                </li>
              )}
            </ul>
          </Group>
          {categories.length > 0 && (
            <Group title="Rubriques">
              <ul>
                {categories.map((c) => (
                  <li key={c.id}>
                    <SmartLink href={`/rubrique/${c.slug}`}>{c.name}</SmartLink>
                  </li>
                ))}
              </ul>
            </Group>
          )}
          <Group title="Informations">
            <ul>
              {LEGAL_LINKS.filter((l) => l.kind !== 'plan').map((l) => (
                <li key={l.slug}>
                  <SmartLink href={`/${l.slug}`}>
                    {pages.find((p) => p.slug === l.slug)?.title ?? l.label}
                  </SmartLink>
                </li>
              ))}
            </ul>
          </Group>
        </div>
      </div>
    </section>
  )
}
